<template>
  <div class="preika-insight-page">
    <section class="preika-page-hero preika-page-hero--workforce">
      <div>
        <p class="preika-page-hero__eyebrow">WORKFORCE ALLOCATION</p>
        <h1>人員×プロジェクト稼働ヒートマップ</h1>
        <p>勤務表の日付単位のプロジェクト設定から、誰が・いつ・どの案件で稼働したかを俯瞰します。</p>
      </div>
      <div class="preika-page-controls">
        <label>
          <span>対象月</span>
          <input v-model="targetMonth" type="month" @change="fetchData">
        </label>
        <button type="button" class="preika-btn preika-btn--primary" :disabled="loading" @click="fetchData">
          <CIcon name="cil-loop"/> 再集計
        </button>
      </div>
    </section>

    <div v-if="loading" class="preika-analytics-loading">
      <span class="preika-analytics-spinner"></span>
      稼働データを集計しています
    </div>

    <div v-else-if="error" class="preika-analytics-error" role="alert">
      <div><strong>稼働ヒートマップを取得できませんでした</strong><small>{{ error }}</small></div>
      <button type="button" @click="fetchData">再取得</button>
    </div>

    <template v-else>
      <section class="preika-analytics-kpis">
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-clock"/></span>
          <small>総稼働時間</small>
          <strong>{{ formatMinutes(data.totalMinutes) }}</strong>
          <p>{{ data.users.length }}名の月次実績</p>
        </article>
        <article class="preika-analytics-kpi">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-layers"/></span>
          <small>稼働プロジェクト</small>
          <strong>{{ data.projects.length }}</strong>
          <p>勤務表に記録された案件</p>
        </article>
        <article class="preika-analytics-kpi" :class="{ 'preika-analytics-kpi--warning': Number(data.unassignedMinutes) > 0 }">
          <span class="preika-analytics-kpi__icon"><CIcon name="cil-warning"/></span>
          <small>プロジェクト未設定</small>
          <strong>{{ formatMinutes(data.unassignedMinutes) }}</strong>
          <p>{{ Number(data.unassignedMinutes) > 0 ? '確認が必要な勤務時間' : '未設定時間はありません' }}</p>
        </article>
      </section>

      <section class="preika-analytics-card">
        <header class="preika-analytics-card__header">
          <div>
            <p>DAILY ASSIGNMENT</p>
            <h2>{{ displayMonth }}の日別稼働</h2>
          </div>
          <div class="preika-heatmap-legend" aria-label="表示凡例">
            <span><i class="project"></i>プロジェクト</span>
            <span><i class="partial"></i>半休</span>
            <span><i class="leave"></i>休暇・休日</span>
            <span><i class="unassigned"></i>未設定</span>
            <span><i class="empty"></i>未入力</span>
          </div>
        </header>

        <div v-if="data.users.length" class="preika-heatmap-scroll">
          <table class="preika-heatmap-table">
            <thead>
              <tr>
                <th class="preika-heatmap-table__person">スタッフ</th>
                <th class="preika-heatmap-table__total">月計</th>
                <th
                  v-for="day in data.days"
                  :key="day.date"
                  :class="{ weekend: day.weekend || day.holidayName }"
                  :title="day.holidayName || ''"
                >
                  <strong>{{ dayNumber(day.date) }}</strong>
                  <small>{{ weekday(day.date) }}</small>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in data.users" :key="user.userId">
                <th class="preika-heatmap-table__person" scope="row">
                  <span class="preika-person-avatar">{{ initial(user.userName) }}</span>
                  <span><strong>{{ user.userName }}</strong><small>{{ user.departmentName || '未所属' }}</small></span>
                </th>
                <td class="preika-heatmap-table__total">
                  <strong>{{ formatMinutes(user.totalMinutes) }}</strong>
                  <small v-if="user.unassignedMinutes">未設定 {{ formatMinutes(user.unassignedMinutes) }}</small>
                </td>
                <td v-for="cell in user.cells" :key="cell.date">
                  <span
                    class="preika-heatmap-cell"
                    :class="'preika-heatmap-cell--' + statusTone(cell.status)"
                    :title="cellTitle(user, cell)"
                  >
                    {{ cellLabel(cell) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="preika-analytics-empty">
          <CIcon name="cil-grid"/>
          <strong>対象月の勤務実績がありません</strong>
          <p>勤務表へ日次情報が登録されると、ここに稼働状況が表示されます。</p>
        </div>
      </section>

      <section class="preika-analytics-card">
        <header class="preika-analytics-card__header">
          <div><p>PROJECT SHARE</p><h2>プロジェクト別稼働構成</h2></div>
          <router-link to="/admin/total/list_project" class="preika-text-link">プロジェクト集計を開く<CIcon name="cil-arrow-right"/></router-link>
        </header>
        <div v-if="data.projects.length" class="preika-allocation-list">
          <article v-for="project in data.projects" :key="project.projectId" class="preika-allocation-row">
            <div class="preika-allocation-row__name">
              <span>{{ initial(project.projectName) }}</span>
              <div><strong>{{ project.projectName || '名称未設定' }}</strong><small>{{ project.headcount }}名が稼働</small></div>
            </div>
            <div class="preika-allocation-row__bar">
              <i :style="{ width: allocationWidth(project.totalMinutes) }"></i>
            </div>
            <strong>{{ formatMinutes(project.totalMinutes) }}</strong>
            <span>{{ Number(project.shareRate || 0).toFixed(1) }}%</span>
          </article>
        </div>
        <div v-else class="preika-analytics-empty preika-analytics-empty--compact">プロジェクト別の稼働実績はありません。</div>
      </section>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import {
  formatManagementMinutes,
  heatmapStatusLabel,
  heatmapStatusTone
} from '../../utils/managementInsights'

export default {
  name: 'WorkforceHeatmap',

  data () {
    return {
      targetMonth: moment().format('YYYY-MM'),
      loading: false,
      error: '',
      data: { yearMonth: '', totalMinutes: 0, unassignedMinutes: 0, days: [], users: [], projects: [] }
    }
  },

  computed: {
    displayMonth () {
      return moment(this.targetMonth, 'YYYY-MM').format('YYYY年M月')
    },

    maxProjectMinutes () {
      return Math.max(1, ...this.data.projects.map(item => Number(item.totalMinutes || 0)))
    }
  },

  mounted () {
    this.fetchData()
  },

  methods: {
    formatMinutes: formatManagementMinutes,
    statusTone: heatmapStatusTone,

    fetchData () {
      const target = moment(this.targetMonth, 'YYYY-MM', true)
      if (!target.isValid()) return
      this.loading = true
      this.error = ''
      return axios
        .get(process.env.VUE_APP_API_ORIGIN + '/management/workforce-heatmap', {
          params: { year: target.format('YYYY'), month: target.format('M') }
        })
        .then(response => {
          const value = response.data || {}
          this.data = {
            yearMonth: value.yearMonth || target.format('YYYYMM'),
            totalMinutes: Number(value.totalMinutes || 0),
            unassignedMinutes: Number(value.unassignedMinutes || 0),
            days: Array.isArray(value.days) ? value.days : [],
            users: Array.isArray(value.users) ? value.users : [],
            projects: Array.isArray(value.projects) ? value.projects : []
          }
        })
        .catch(error => {
          this.error = error.response && error.response.status === 403
            ? 'この画面を表示する権限がありません。'
            : '通信または集計処理でエラーが発生しました。'
          console.error('稼働ヒートマップ取得失敗:', error)
        })
        .finally(() => { this.loading = false })
    },

    dayNumber (date) {
      return moment(date, 'YYYY-MM-DD').format('D')
    },

    weekday (date) {
      return ['日', '月', '火', '水', '木', '金', '土'][moment(date, 'YYYY-MM-DD').day()]
    },

    initial (value) {
      return String(value || '—').substring(0, 1)
    },

    cellLabel (cell) {
      if (cell.status === 'PROJECT') return this.initial(cell.projectName)
      return heatmapStatusLabel(cell.status)
    },

    cellTitle (user, cell) {
      const project = cell.projectName ? ` / ${cell.projectName}` : ''
      const attendance = cell.attendanceName ? ` / ${cell.attendanceName}` : ''
      return `${user.userName} / ${cell.date}${project}${attendance} / ${this.formatMinutes(cell.workMinutes)}`
    },

    allocationWidth (minutes) {
      return `${Math.max(3, Number(minutes || 0) / this.maxProjectMinutes * 100)}%`
    }
  }
}
</script>
